import { useEffect, useState } from 'react';

import { Popover, Stack, TextField } from '@mui/material';

import FilterButton from './FilterButton';
import { useFilterAnchor } from './useFilterAnchor';

import { FILTER_DEBOUNCE_TIME_MS } from '@/app/constants';

interface MinMaxFilterProps {
  filterId: string;
  buttonAriaLabel: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  searchParamMinValue: number | undefined;
  searchParamMaxValue: number | undefined;
  handleNavigation: (
    minValue: number | undefined,
    maxValue: number | undefined
  ) => void;
}

const MinMaxFilter = ({
  filterId,
  buttonAriaLabel,
  minPlaceholder = 'Min Value',
  maxPlaceholder = 'Max Value',
  searchParamMinValue,
  searchParamMaxValue,
  handleNavigation,
}: MinMaxFilterProps) => {
  const { anchorEl, handleOpen, handleClose, open, popoverId } =
    useFilterAnchor(filterId);

  const [numbers, setNumbers] = useState({
    minValue: searchParamMinValue,
    maxValue: searchParamMaxValue,
  });
  const [isChangedInternally, setIsChangedInternally] = useState(false);

  const handleNumberChange = (key: 'minValue' | 'maxValue', value: string) => {
    setIsChangedInternally(true);
    const newValue = value ? Number(value) : undefined;
    setNumbers((prev) => ({
      ...prev,
      [key]: newValue,
    }));
  };

  useEffect(() => {
    setNumbers({
      minValue: searchParamMinValue,
      maxValue: searchParamMaxValue,
    });
    setIsChangedInternally(false);
  }, [searchParamMinValue, searchParamMaxValue]);

  useEffect(() => {
    if (!isChangedInternally) {
      return;
    }

    const { minValue, maxValue } = numbers;
    const timer = setTimeout(() => {
      setIsChangedInternally(false);
      handleNavigation(minValue, maxValue);
    }, FILTER_DEBOUNCE_TIME_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [
    handleNavigation,
    isChangedInternally,
    numbers,
    searchParamMaxValue,
    searchParamMinValue,
  ]);

  return (
    <>
      <FilterButton
        isActive={
          numbers.minValue !== undefined || numbers.maxValue !== undefined
        }
        onClick={handleOpen}
        aria-label={buttonAriaLabel}
      />
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Stack px={3} py={2.5} gap={1.5}>
          <TextField
            label={minPlaceholder}
            type='number'
            variant='outlined'
            size='small'
            value={numbers.minValue ?? ''}
            onChange={(e) => {
              handleNumberChange('minValue', e.target.value);
            }}
            fullWidth
            slotProps={{
              htmlInput: {
                min: 0,
              },
            }}
          />
          <TextField
            label={maxPlaceholder}
            type='number'
            variant='outlined'
            size='small'
            value={numbers.maxValue ?? ''}
            onChange={(e) => {
              handleNumberChange('maxValue', e.target.value);
            }}
            fullWidth
            slotProps={{
              htmlInput: {
                min: 0,
              },
            }}
          />
        </Stack>
      </Popover>
    </>
  );
};

export default MinMaxFilter;
